import React, { useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, fmtDate, useAdminGet } from '../../kit';
import { Badge, Drawer } from '../emailKit';
import { tagCell, tagText } from '../../tags/shared';
import { PEOPLE, SourceRef, TimelineTarget, emailPeople, num } from './shared';

/**
 * Every import (people/imports), newest first, and each one's rows
 * (people/import_detail): what happened to every line of the file and who the
 * person is now.
 */

const ROW_STATUS: Record<string, { label: string; tone: 'green' | 'blue' | 'amber' | 'red' | 'grey' }> = {
  created: { label: 'created', tone: 'green' },
  updated: { label: 'updated', tone: 'blue' },
  unchanged: { label: 'matched, unchanged', tone: 'grey' },
  duplicate_in_file: { label: 'duplicate in file', tone: 'amber' },
  invalid: { label: 'invalid', tone: 'red' },
};

const BatchRows: React.FC<{
  batch: any; onClose: () => void; onOpen: (t: TimelineTarget) => void; onViewPeople: (s: SourceRef) => void;
}> = ({ batch, onClose, onOpen, onViewPeople }) => {
  const [status, setStatus] = useState('');
  const res = useAdminGet<any>(PEOPLE, 'import_detail', { batch_id: batch.id, status }, [batch.id, status]);
  // `state` as text is what the CSV carries; the cell renders the pills.
  const rows: any[] = (res.data?.rows || []).map((r: any) => ({
    ...r, state: r.lifecycle ? [r.lifecycle_label, tagText(r)].filter(Boolean).join(', ') : '',
  }));
  const byStatus: Record<string, number> = res.data?.by_status || {};
  const people = rows.filter(r => r.user_id && r.status !== 'invalid' && r.status !== 'duplicate_in_file');
  return (
    <Drawer title={`Import “${batch.source_label}”`}
      subtitle={`${batch.filename} · ${fmtDate(batch.created_at)}${batch.created_by ? ` · by ${batch.created_by}` : ''}`}
      onClose={onClose}>
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        <button type="button" onClick={() => setStatus('')}
          className={`rounded-full px-2 py-0.5 border ${!status ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300'}`}>
          All rows ({num(Object.values(byStatus).reduce((a, b) => a + b, 0))})
        </button>
        {Object.entries(ROW_STATUS).map(([k, s]) => (
          <button key={k} type="button" onClick={() => setStatus(status === k ? '' : k)}
            className={`rounded-full px-2 py-0.5 border ${status === k ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300'}`}>
            {s.label} ({num(byStatus[k])})
          </button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Btn small onClick={() => onViewPeople({ id: batch.id, label: batch.source_label })}>View in the directory</Btn>
        <Btn small disabled={!people.length} onClick={() => emailPeople(people.map(r => r.user_id))}>
          E-mail {status ? 'these' : 'the imported people'} ({num(people.length)})
        </Btn>
      </div>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <DataTable rows={rows} csvName={`people_import_${batch.id}`} maxHeight="65vh" empty="No rows."
          columns={['line', 'status', 'email', 'state', 'detail', 'error']}
          labels={{ line: 'Row', state: 'State now', detail: 'What happened' }}
          render={{
            status: r => (
              <span className="inline-flex gap-1">
                <Badge tone={ROW_STATUS[r.status]?.tone || 'grey'}>{ROW_STATUS[r.status]?.label || r.status}</Badge>
                {r.team && <Badge tone="grey">team</Badge>}
              </span>
            ),
            email: r => (
              <button type="button" className="underline underline-offset-2 text-left"
                onClick={() => onOpen(r.user_id ? { userId: r.user_id } : { email: r.email })}>{r.email}</button>
            ),
            state: r => (r.lifecycle ? tagCell(r) : '—'),
          }} />
      )}
    </Drawer>
  );
};

const ImportHistory: React.FC<{ onViewPeople: (s: SourceRef) => void; onOpen: (t: TimelineTarget) => void }> =
  ({ onViewPeople, onOpen }) => {
  const res = useAdminGet<any>(PEOPLE, 'imports');
  const [open, setOpen] = useState<any>(null);
  const rows: any[] = res.data?.rows || [];
  return (
    <Section title={`Imports (${num(rows.length)})`} right={<Btn small onClick={res.reload}>Refresh</Btn>}>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <DataTable rows={rows} csvName="people_imports" empty="Nothing imported yet — use the Import tab."
          columns={['id', 'source_label', 'filename', 'created_by', 'created_at', 'rows', 'created', 'updated',
            'unchanged', 'duplicate_in_file', 'invalid', 'team', 'tags_assigned']}
          labels={{ id: '#', source_label: 'Label', filename: 'File', created_by: 'By', created_at: 'When',
            created: 'New', duplicate_in_file: 'Duplicates', tags_assigned: 'Tags added' }}
          actions={r => (
            <span className="inline-flex gap-1">
              <Btn small onClick={() => setOpen(r)}>Rows</Btn>
              <Btn small onClick={() => onViewPeople({ id: r.id, label: r.source_label })}>View people</Btn>
            </span>
          )} />
      )}
      {open && (
        <BatchRows batch={open} onClose={() => setOpen(null)} onOpen={onOpen}
          onViewPeople={s => { setOpen(null); onViewPeople(s); }} />
      )}
    </Section>
  );
};

export default ImportHistory;
